import React, { useContext, forwardRef } from 'react';
import { Filter as FilterIcon } from 'react-feather';
import styled from '@emotion/styled';
import { AppThemingContext } from '@trinity/chat-components/containers/AppTheming';
import { shouldForwardProp } from '../../utils/emotion';

export type ChatFilterButtonVariant = 'chart' | 'survey';

type Props = {
  css?: CSSOverrides;
  onClick?: (e: React.MouseEvent) => void;
  disabled?: boolean;
  count?: number;
  variant?: ChatFilterButtonVariant;
  className?: string;
  'aria-label'?: string;
  type?: 'button' | 'submit' | 'reset';
  children?: React.ReactNode;
};

export const ChatFilterButton = forwardRef<HTMLButtonElement, Props>((props, ref) => {
  const {
    css = {},
    onClick,
    disabled = false,
    count = 0,
    variant = 'chart',
    className,
    'aria-label': ariaLabel = 'Filter',
    type = 'button',
  } = props;

  const isActive = count > 0;
  const themingContext = useContext(AppThemingContext);

  const enabled = themingContext?.enabled;
  const themeColor = enabled ? themingContext.palette?.primary?.main : null;
  const hasCustomColor = !!themeColor;

  if (variant === 'survey') {
    return renderChatSurveyVariant({
      css,
      ref,
      onClick,
      disabled,
      ariaLabel,
      type,
      className,
      isActive,
      hasCustomColor,
      themeColor,
      count,
    });
  }

  return renderChatChartVariant({
    css,
    ref,
    onClick,
    disabled,
    ariaLabel,
    type,
    className,
    isActive,
    hasCustomColor,
    themeColor,
    count,
  });
});

type ChatVariantProps = {
  css: CSSOverrides;
  ref: React.Ref<HTMLButtonElement>;
  onClick?: (e: React.MouseEvent) => void;
  disabled: boolean;
  ariaLabel: string;
  type: 'button' | 'submit' | 'reset';
  className?: string;
  isActive: boolean;
  hasCustomColor: boolean;
  themeColor: string | null;
  count: number;
};

function renderChatSurveyVariant({
  css,
  ref,
  onClick,
  disabled,
  ariaLabel,
  type,
  className,
  isActive,
  hasCustomColor,
  themeColor,
  count,
}: ChatVariantProps) {
  return (
    <SurveyButton
      css={css}
      ref={ref}
      onClick={disabled ? undefined : onClick}
      aria-label={ariaLabel}
      type={type}
      disabled={disabled}
      className={className}
      $isActive={isActive}
      $hasCustomColor={hasCustomColor}
      $themeColor={themeColor}
    >
      <SurveyIconContainer>
        <StyledFilterIcon height={20} width={20} $themeColor={themeColor} />
        <SurveyActiveCircle $isActive={isActive} />
      </SurveyIconContainer>
      <SurveyText>Filter {isActive ? `(${count})` : ''}</SurveyText>
    </SurveyButton>
  );
}

function renderChatChartVariant({
  css,
  ref,
  onClick,
  disabled,
  ariaLabel,
  type,
  className,
  isActive,
  hasCustomColor,
  themeColor,
  count,
}: ChatVariantProps) {
  return (
    <ChartButton
      css={css}
      ref={ref}
      onClick={disabled ? undefined : onClick}
      aria-label={ariaLabel}
      type={type}
      disabled={disabled}
      className={isActive ? 'active' : className}
      $isActive={isActive}
      $hasCustomColor={hasCustomColor}
      $themeColor={themeColor}
    >
      <ChartIconContainer $isActive={isActive} $hasCustomColor={hasCustomColor}>
        <StyledFilterIcon
          height={20}
          width={20}
          $isActive={isActive}
          $hasCustomColor={hasCustomColor}
          $themeColor={themeColor}
        />
        <ChartActiveCircle
          className={isActive ? 'active' : ''}
          $isActive={isActive}
          $hasCustomColor={hasCustomColor}
          $themeColor={themeColor}
        />
      </ChartIconContainer>
      {isActive && <ChartDivider />}
      <ChartText $isActive={isActive} $hasCustomColor={hasCustomColor}>
        {isActive ? `(${count})` : ''}
      </ChartText>
    </ChartButton>
  );
}

ChatFilterButton.displayName = 'ChatFilterButton';

// Chart variant styles
const ChartButton = styled('button', {
  shouldForwardProp: shouldForwardProp('$isActive', '$hasCustomColor', '$themeColor'),
})<{
  $isActive?: boolean;
  $hasCustomColor?: boolean;
  $themeColor?: string;
}>(({ theme, $isActive, $hasCustomColor, $themeColor }) => {
  const _borderColor = $hasCustomColor ? 'var(--gray-m)' : 'var(--gray-m)';
  const backgroundColor = $hasCustomColor ? 'var(--gray-xl)' : theme?.palette?.white?.main || '#fff';
  const textColor = $hasCustomColor ? 'var(--gray-d)' : 'var(--gray-d)';

  return {
    display: 'flex',
    alignItems: 'center',
    background: backgroundColor,
    padding: '8px 8px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontWeight: 500,
    fontSize: '14px',
    transition: 'all 0.2s ease',
    outline: 'none',
    borderRadius: '6px',
    color: textColor,
    whiteSpace: 'nowrap',
    border: 'none',
    '&.active': {
      background: $hasCustomColor ? 'var(--gray-l)' : 'var(--blue-l)',
      borderColor: $hasCustomColor ? 'var(--gray-d)' : 'var(--blue-m)',
      color: $hasCustomColor ? 'var(--gray-d)' : 'var(--blue-d)',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.15)',
    },
    '&:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed',
      color: 'var(--gray-l)',
      boxShadow: 'none',
      '&:active, &.active': {
        background: 'none',
        color: 'inherit',
      },
    },
  };
});

const ChartIconContainer = styled('span', {
  shouldForwardProp: shouldForwardProp('$isActive', '$hasCustomColor'),
})<{
  $isActive?: boolean;
  $hasCustomColor?: boolean;
}>({
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
  height: '20px',
  paddingRight: '4px',
  marginRight: '0px',
});

const ChartActiveCircle = styled('span', {
  shouldForwardProp: shouldForwardProp('$isActive', '$hasCustomColor', '$themeColor'),
})<{
  $isActive?: boolean;
  $hasCustomColor?: boolean;
  $themeColor?: string;
}>(({ $isActive, $hasCustomColor, $themeColor }) => {
  const circleColor = $hasCustomColor ? $themeColor || 'var(--pri-02)' : 'var(--pri-02)';

  return {
    position: 'absolute',
    height: '6px',
    width: '6px',
    borderRadius: '8px',
    backgroundColor: circleColor,
    boxSizing: 'content-box',
    right: '5px',
    top: '0px',
    border: `2px solid var(--gray-xl)`,
    display: 'none',
    '&.active': {
      display: 'inline',
    },
  };
});

const ChartText = styled('span', {
  shouldForwardProp: shouldForwardProp('$isActive', '$hasCustomColor'),
})<{
  $isActive?: boolean;
  $hasCustomColor?: boolean;
}>(({ $isActive, $hasCustomColor }) => {
  const textColor = $hasCustomColor ? 'var(--gray-d)' : 'inherit';

  return {
    fontSize: '14px',
    fontWeight: 'bold',
    whiteSpace: 'nowrap',
    color: textColor,
  };
});

const ChartDivider = styled.div({
  width: '1px',
  height: '16px',
  backgroundColor: 'var(--gray-m)',
  marginLeft: '4px',
  marginRight: '4px',
});

// Survey variant styles
const SurveyButton = styled('button', {
  shouldForwardProp: shouldForwardProp('$isActive', '$hasCustomColor', '$themeColor'),
})<{
  $isActive?: boolean;
  $hasCustomColor?: boolean;
  $themeColor?: string;
}>(({ theme, $isActive, $hasCustomColor, $themeColor }) => ({
  display: 'flex',
  alignItems: 'center',
  background: theme?.palette?.white?.main || '#fff',
  padding: '8px 12px',
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontWeight: 500,
  fontSize: '14px',
  transition: 'all 0.2s ease',
  outline: 'none',
  borderRadius: '6px',
  border: `1px solid ${$isActive && $hasCustomColor ? $themeColor : 'var(--gray-m)'}`,
  color: $isActive && $hasCustomColor ? $themeColor : 'inherit',
  '&:hover': {
    opacity: 0.8,
  },
  '&:disabled': {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
}));

const SurveyIconContainer = styled.span({
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
  marginRight: '8px',
});

const SurveyActiveCircle = styled('span', {
  shouldForwardProp: shouldForwardProp('$isActive'),
})<{
  $isActive?: boolean;
}>(({ $isActive }) => ({
  position: 'absolute',
  height: '6px',
  width: '6px',
  borderRadius: '50%',
  backgroundColor: 'var(--pri-02)',
  right: '-2px',
  top: '-2px',
  border: '1px solid white',
  display: $isActive ? 'block' : 'none',
}));

const SurveyText = styled.span({
  fontSize: '14px',
  fontWeight: 500,
});

// Shared icon styles
const StyledFilterIcon = styled(FilterIcon, {
  shouldForwardProp: shouldForwardProp('$isActive', '$hasCustomColor', '$themeColor'),
})<{
  $isActive?: boolean;
  $hasCustomColor?: boolean;
  $themeColor?: string;
}>(({ $isActive, $hasCustomColor, $themeColor }) => {
  const iconColor = 'var(--gray-d)';
  return {
    color: $themeColor || iconColor,
    stroke: $themeColor || iconColor,
    fill: 'none',
  };
});
